// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import { Command } from "graphql/api";
import { DataState } from "types";

export interface ISchedulerItem {
  lastRequest?: number; // ms
  lastData?: number; // ms
}

export interface IPendingCommand {
  timestampMs: number;
  seqNr: number; // derived from timestamp, but some modulo to keep inside int data range
  command: Command;
  cmdArgs: any; // depends on the command
}

export enum AppState {
  INIT = 0, // startup state
  LOGGED_OUT = 1, // application files loaded, sagas initialized, but not logged in
  LOGGED_IN = 2, // logged in, but no site selected
  SITE_LIST_LOADED = 3, // site and user list loaded
  SITE_SELECTED = 4, // site selected, but no parameter/charger list loaded
  PARAMETER_LOADED = 5, // parameter, charger list, rfids, tariffs, and open charging sessions loaded
  LIVE_DATA_SUBSCRIBED = 6, // trendinfo ready, subscribed to enline and charger live data
  READY = 7, // enline live data received
}

export enum UserState {
  UNKNOWN = 0, // not signed-up, not signed-in, logged out
  SINGING_UP, // user wants to sign-up; not signed-up, not logged-in, still logged out!
  SINGING_UP_CONFIRM, // user wants to confirm sign-up; not signed-up, not logged-in, still logged out!
  FORGOT_PASSWORD, // not signed-up, not signed-in, logged out -> requests a Password-Code
  // all others are logged-in states
  PASSWORD_CONFIRM, // not signed-up, not signed-in, logged out -> Password-Code verification!
  PASSWORD_CHANGED, // password was successfully changed (after Forgot Password) - user not logged in
  NEEDS_PASSWORD_CHANGE, // logged-in -> forced to change the password (1st login, when created by Admin)
  CHANGING_PASSWORD, // logged-in -> requested a password-change
  CREATE_NEW_USER, // logged-in -> request to create a new user (payload->id == 0)!
}

/* Scheduler states */
export interface IStateScheduler {
  appState: AppState;
  userState: UserState;
  isInBackground: boolean;
  login: ISchedulerItem;
  loginDataState: DataState;
  siteList: ISchedulerItem;
  userListBasic: ISchedulerItem;
  userListExt: ISchedulerItem;
  parameter: ISchedulerItem;
  parameterDataState: DataState;
  trendLoading: ISchedulerItem;
  trendLoadingState: DataState;
  trendLoadingProgress: number; // 0..1
  ioTrendLoading: ISchedulerItem;
  isIoTrendLoading: boolean;
  allData: ISchedulerItem;
  alarm: ISchedulerItem;
  controllerData: ISchedulerItem;
  setOutput: ISchedulerItem;
  saveState: DataState; // save state for arbitraty data items
  pendingCommand: IPendingCommand | undefined;
  isOffline: boolean;
  ocppChargerList: ISchedulerItem;
  ocppConnectorData: ISchedulerItem;
  ocppRfidListBasic: ISchedulerItem;
  ocppRfidListExt: ISchedulerItem;
  ocppTariffList: ISchedulerItem;
  pendingOcppSessionState: ISchedulerItem;
  ocppSessionState: DataState;
  chargingSession: ISchedulerItem;
  chargingPower: ISchedulerItem;
  fileUploadLoading: ISchedulerItem;
  isFileUploadLoading: boolean;
}

/* initial state */
export const INITIAL_STATE: IStateScheduler = {
  appState: AppState.INIT,
  userState: UserState.UNKNOWN,
  isInBackground: false,
  login: {},
  loginDataState: DataState.EMPTY,
  siteList: {},
  userListBasic: {},
  userListExt: {},
  parameter: {},
  parameterDataState: DataState.EMPTY,
  trendLoading: {},
  trendLoadingState: DataState.EMPTY,
  trendLoadingProgress: 0,
  ioTrendLoading: {},
  isIoTrendLoading: false,
  allData: {},
  alarm: {},
  controllerData: {},
  setOutput: {},
  saveState: DataState.EMPTY,
  pendingCommand: undefined,
  isOffline: false,
  ocppChargerList: {},
  ocppConnectorData: {},
  ocppRfidListBasic: {},
  ocppRfidListExt: {},
  ocppTariffList: {},
  pendingOcppSessionState: {},
  ocppSessionState: DataState.EMPTY,
  chargingSession: {},
  chargingPower: {},
  fileUploadLoading: {},
  isFileUploadLoading: false,
};
